import {
	type IReactionDisposer,
	autorun,
	action,
	computed,
	makeObservable,
	observable,
	runInAction,
} from 'mobx';

// TODO: see if this can be reused should s3 multipart suffer the same queue issues

import * as tus from 'tus-js-client';

export type TusQueueItem = {
	id: string;
	paused?: boolean;
	// TODO: we can break this all down a bit more with params and callbacks
	// then whatever uses this queue can decide what happens when an item becomes
	// active/inactive/paused/unpaused
	// onItemActive/onItemInactive/onItemPause/onItemResume/onPause/onResume
	// then this queue just sets the active/paused props and the outside handles the upload start/abort piece
	// no reson this code needs to be tied directly to tus or uploads for that matter
	uploader: tus.Upload;
	active?: boolean;
};

type TusQueueOptions = {
	limit?: number;
};

/**
 * This is not your typical queue with a `next` command\
 * This queue will manage itself depending on the state of the items within
 */
export class TusQueue {
	#disposers: IReactionDisposer[] = [];
	queue = observable.array<TusQueueItem>([]);
	paused = false;
	delayTimeout: NodeJS.Timeout | null = null;
	limit: number;

	get currentItems() {
		return this.queue.filter((item) => item.active);
	}

	get hasActiveItems() {
		return this.currentItems.length > 0;
	}

	get hasItems() {
		return this.queue.length > 0;
	}

	get queuedIds() {
		return this.queue.map((item) => item.id);
	}

	constructor({limit = 1}: TusQueueOptions = {}) {
		this.limit = limit;

		makeObservable(
			this,
			{
				add: action,
				currentItems: computed,
				hasActiveItems: computed,
				hasItems: computed,
				pauseAllItems: action,
				pauseItem: action,
				queue: observable,
				queuedIds: computed,
				pause: action,
				paused: observable,
				remove: action,
				reset: action,
				resume: action,
				resumeAllItems: action,
				resumeItem: action,
				toggleAllItems: action,
				toggleItem: action,
				updateItem: action,
			},
			{
				deep: true,
			},
		);

		this.#disposers.push(
			autorun(() => {
				if (this.paused) return;

				const canBeActiveItems = this.queue.filter(
					(item) => !item.paused && !item.active && item.uploader.file != null,
				);
				const canBeActiveCount = canBeActiveItems.length;
				if (this.currentItems.length >= limit || !canBeActiveCount) return;
				const _limit = Math.min(canBeActiveCount, limit);
				runInAction(() => {
					for (let i = 0; i < _limit; i++) {
						canBeActiveItems[i].active = true;
						canBeActiveItems[i].uploader.start();
					}
				});
			}),
		);
	}

	/**
	 * Add item to queue
	 * @param item Queue Item
	 */
	add(item: Omit<TusQueueItem, 'active'>) {
		this.queue.replace([...this.queue, {paused: false, ...item}]);
	}

	/**
	 * Get item from queue
	 * @param id Item id
	 * @returns Queue Item
	 */
	getItem(id: string) {
		return this.queue.find((item) => item.id === id);
	}

	/**
	 * Pause queue
	 * @param delay Optional timeout to auto resume
	 */
	pause(delay?: number) {
		this.delayTimeout && clearTimeout(this.delayTimeout);
		this.paused = true;
		this.queue.replace(
			this.queue.map((item) => {
				// only abort and set to inactive
				// let the pause state remain as it was
				if (item.active) {
					item.uploader.abort();
					return {...item, active: false};
				}
				return item;
			}),
		);

		if (delay) {
			this.delayTimeout = setTimeout(this.resume.bind(this), delay);
		}
	}

	/**
	 * Pause all items in queue
	 */
	pauseAllItems() {
		this.toggleAllItems(true);
	}

	/**
	 * Pause item in queue
	 * @param id Item id
	 */
	pauseItem(id: string) {
		this.toggleItem(id, true);
	}

	/**
	 * Remove item from queue
	 * @param id Item id
	 */
	remove(id: string, noTerminate = false) {
		const item = this.getItem(id);
		if (!item) return;
		item.uploader.abort(noTerminate === false);
		this.queue.replace(this.queue.filter((item) => item.id !== id));
	}

	/**
	 * Reset queue
	 */
	reset() {
		this.queue.forEach((item) => item.active && item.uploader.abort());
		this.queue.replace([]);
	}

	/**
	 * Resume queue
	 */
	resume() {
		this.delayTimeout && clearTimeout(this.delayTimeout);
		this.paused = false;
	}

	/**
	 * Resume all items in queue
	 */
	resumeAllItems() {
		this.toggleAllItems(false);
	}

	/**
	 * Resume item in queue
	 * @param id Item id
	 */
	resumeItem(id: string) {
		this.toggleItem(id, false);
	}

	/**
	 * Toggle all items in queue
	 * @param paused
	 */
	toggleAllItems(paused?: boolean) {
		this.queue.replace(
			this.queue.map((item) => {
				const isPaused = paused != null ? paused : !item.paused;
				// if (isPaused && item.active) item.uploader.abort();
				if (isPaused) item.uploader.abort();
				return {
					...item,
					active: isPaused ? false : item.active,
					// only not paused if we have a file - file could be missing and needs reselection
					paused: isPaused ? true : item.uploader.file == null,
				};
			}),
		);
	}

	/**
	 * Toggle item in queue
	 * @param id Item id
	 * @param paused
	 */
	toggleItem(id: string, paused?: boolean) {
		const item = this.getItem(id);
		if (!item) return;

		const isPaused = paused != null ? paused : !item.paused;
		// if (isPaused && item.active) {
		if (isPaused) {
			item.active = false;
			item.uploader.abort();
		}

		item.paused = isPaused;
	}

	/**
	 * Update item in queue
	 *
	 * We add all items to the queue, regardless of state, and may need t oupdate some data after the fact\
	 * See TODOs above - if that gets handled, this method goes away as it has nothing to do with queueing and
	 * everything to do with tus
	 * @param id Item id
	 * @param data
	 * @returns
	 */
	updateItem(
		id: string,
		data: {
			file: TusQueueItem['uploader']['file'];
		},
	) {
		const item = this.getItem(id);
		if (!item) return;
		item.uploader.file = data.file;
	}

	/**
	 * Cleanup
	 */
	dispose() {
		this.delayTimeout && clearTimeout(this.delayTimeout);
		this.#disposers.forEach((disposer) => disposer());
		this.reset();
	}
}

export default TusQueue;
